/*
    Copyright 2022-2025, Techwise Integrated Security, LLC
*/

import React, { useState } from "react";

import { Box, Button, CircularProgress, Grid, IconButton, TextField, Tooltip, Typography } from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import EditIcon from "@mui/icons-material/Edit";
import { Effect, Nullable, Setter } from "../../utils/Interfaces";
import { modifyField } from "./formControlsFunctions";
import { useAppDispatch } from "../../app/hooks";
import { AnyAction, Dispatch } from "@reduxjs/toolkit";

export interface MobileParagraphEditProps<ParentModel> {
    field: Nullable<string>;
    updatePath: string;
    readonly: boolean;
    updateKey: keyof ParentModel;
    label: string;
    onUpdated: Setter<Effect>;
    showNA?: boolean;
    lines?: number;
    editIcon?: JSX.Element;
}

const MobileParagraphEdit = <ParentModel,>(props: MobileParagraphEditProps<ParentModel>) => {
    const { field, readonly, updatePath, updateKey, label, onUpdated, showNA = true, lines = 4, editIcon = <EditIcon /> } = props;

    const dispatch: Dispatch<AnyAction> = useAppDispatch();

    const [editing, setEditing] = useState<boolean>(false);
    const [state, setState] = useState<Nullable<string>>(field);

    const [loading, setLoading] = useState<boolean>(false);

    let darkTheme = localStorage.getItem("techwiseTheme") === "dark";

    return (
        <Box sx={{ backgroundColor: darkTheme ? "#303030" : "#F2F2F2", borderRadius: 2, pl: 2, pr: 2, pb: 2 }}>
            <Grid container direction="row" justifyContent="space-between" alignItems="center">
                <Typography fontWeight="bold">{label}</Typography>

                <Box>
                    {(readonly || !editing) && (
                        <Box>
                            {showNA && (field === null || field === undefined || field === "") && (
                                <>
                                    {!loading && (
                                        <Tooltip title={`${label} is N/A`}>
                                            <Button
                                                onClick={() => {
                                                    setLoading(true);
                                                    setState("N/A");
                                                    modifyField<ParentModel>(updatePath, updateKey, "N/A", dispatch, () => {
                                                        onUpdated(() => {
                                                            setLoading(false);
                                                            setEditing(false);
                                                        });
                                                    });
                                                }}
                                            >
                                                N/A
                                            </Button>
                                        </Tooltip>
                                    )}
                                </>
                            )}
                            {!loading && (
                                <Tooltip title={`Edit ${label}`}>
                                    <IconButton onClick={() => setEditing(true)} size="large">
                                        {editIcon}
                                    </IconButton>
                                </Tooltip>
                            )}
                            {loading && <CircularProgress size={16} sx={{ mt: 1 }} />}
                        </Box>
                    )}
                    {!readonly && editing && (
                        <Box>
                            {loading && <CircularProgress size={16} sx={{ mt: 1 }} />}
                            {!loading && (
                                <Tooltip title="Cancel Changes">
                                    <IconButton
                                        onClick={() => {
                                            setState(field);
                                            setEditing(false);
                                        }}
                                    >
                                        <CloseIcon />
                                    </IconButton>
                                </Tooltip>
                            )}
                            {!loading && (
                                <Tooltip title={`Update ${label}`}>
                                    <IconButton
                                        onClick={() => {
                                            setLoading(true);
                                            modifyField<ParentModel>(updatePath, updateKey, state, dispatch, () => {
                                                onUpdated(() => {
                                                    setLoading(false);
                                                    setEditing(false);
                                                });
                                            });
                                        }}
                                    >
                                        <CheckIcon />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </Box>
                    )}
                </Box>
            </Grid>
            {(readonly || !editing) && <Typography>{field ? field : "--"}</Typography>}
            {!readonly && editing && (
                <TextField
                    disabled={loading}
                    multiline
                    rows={lines}
                    fullWidth
                    margin="normal"
                    id={updateKey as string}
                    name={updateKey as string}
                    value={state}
                    label={label}
                    size="small"
                    onBlur={() => {
                        modifyField<ParentModel>(updatePath, updateKey, state, dispatch, () => {
                            onUpdated(() => {
                                setLoading(false);
                                setEditing(false);
                            });
                        });
                    }}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => setState(event.target.value)}
                />
            )}
        </Box>
    );
};

export default MobileParagraphEdit;
